import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Trash2, Mic, FileText, Trash, Ban, CalendarClock, Undo2 } from "lucide-react";
import Markdown from "../../components/Markdown";
import VoiceRecorder from "../../components/VoiceRecorder";
import RosterProgressCard from "../../components/RosterProgressCard";
import PlanBriefFields from "../../components/PlanBriefFields";

function toDateInput(isoStr) {
  if (!isoStr) return "";
  const s = isoStr.replace("Z", "");
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function toTimeInput(isoStr) {
  if (!isoStr) return "";
  const s = isoStr.replace("Z", "");
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}
function combineNaiveIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return "";
  return `${dateStr}T${timeStr}:00`;
}

export default function CoachLessonDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [student, setStudent] = useState(null);
  const [plan, setPlan] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [showBrief, setShowBrief] = useState(false);
  const [brief, setBrief] = useState({
    focus: "", duration_mins: "", group_size: "", courts: "", skill_level: "",
    notes: "", title: "", student_ids: [], student_id: "",
  });
  const [observations, setObservations] = useState("");
  const [insights, setInsights] = useState("");
  const [insightLoading, setInsightLoading] = useState(false);

  // Notes log
  const [notesLog, setNotesLog] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [notePolish, setNotePolish] = useState(true);
  const [notePosting, setNotePosting] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);

  // Cancel + reschedule
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [resched, setResched] = useState({ date: "", time: "", notify: true });
  const [reschedBusy, setReschedBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/lessons/${id}`);
      setLesson(data);
      setPlan(data.lesson_plan || "");
      setObservations(data.notes || "");
      setInsights(data.insights || "");
      setNotesLog(data.notes_log || []);
      // Seed the plan brief from the event, but never overwrite what the coach
      // has already typed if this runs again after an unrelated action.
      setBrief((cur) => ({
        focus: cur.focus || "",
        duration_mins: cur.duration_mins || data.duration_mins || 60,
        group_size: cur.group_size || data.max_students || 1,
        courts: cur.courts || data.courts || 1,
        skill_level: cur.skill_level ?? "",
        notes: cur.notes || "",
        title: cur.title || data.title || "",
        student_ids: (cur.student_ids && cur.student_ids.length)
          ? cur.student_ids
          : (data.booked_students || []),
        student_id: cur.student_id || data.student_id || "",
      }));
      if (data.student_id) {
        const { data: s } = await api.get(`/students/${data.student_id}`);
        setStudent(s);
      }
    } catch (_) {}
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/students").then(({ data }) => setStudents(data || [])).catch(() => {});
  }, []);

  // Group events and small-group private lessons get the full plan brief.
  const rosterCapable = !!lesson && (
    ["clinic", "mixer", "open_play"].includes(lesson.type) ||
    (lesson.type === "private" &&
      ((lesson.booked_students || []).length >= 2 || (lesson.max_students || 1) >= 2))
  );

  const savePlan = async () => {
    try {
      await api.patch(`/lessons/${id}`, { lesson_plan: plan });
      toast.success("Plan saved");
    } catch (_) { toast.error("Save failed"); }
  };

  const generatePlan = async () => {
    setGenLoading(true);
    try {
      const body = {
        lesson_id: id,
        student_id: brief.student_id || lesson?.student_id || null,
        // Explicit roster selection replaces the lesson roster (an empty array
        // means "use the lesson roster", so only send it when group-capable).
        student_ids: rosterCapable ? (brief.student_ids || []) : undefined,
        focus: brief.focus,
        duration_mins: Number(brief.duration_mins) || lesson.duration_mins || 60,
        lesson_type: lesson.type,
        title: brief.title || lesson.title,
        skill_level:
          brief.skill_level === "" || brief.skill_level == null
            ? null
            : Number(brief.skill_level),
        group_size: Number(brief.group_size) || lesson.max_students || 1,
        courts: Number(brief.courts) || lesson.courts || null,
        notes: brief.notes,
      };
      const { data } = await api.post("/ai/lesson-plan", body);
      setPlan(data.plan);
      const tag = data.roster_size >= 2 ? `roster-aware for ${data.roster_size} players` : "plan generated";
      toast.success(tag);
    } catch (_) { toast.error("AI generation failed"); }
    setGenLoading(false);
  };

  const complete = async () => {
    try {
      await api.post(`/lessons/${id}/complete`);
      toast.success("Lesson marked complete");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not complete"); }
  };

  const uncomplete = async () => {
    try {
      await api.post(`/lessons/${id}/uncomplete`);
      toast.success("Lesson reopened");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not reopen"); }
  };

  const generateInsight = async () => {
    if (!observations.trim()) { toast.error("Write observations first"); return; }
    setInsightLoading(true);
    try {
      const { data } = await api.post("/ai/insight", { lesson_id: id, observations });
      setInsights(data.insights);
      toast.success("Insights saved");
    } catch (_) { toast.error("AI failed"); }
    setInsightLoading(false);
  };

  const remove = async () => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      await api.delete(`/lessons/${id}`);
      toast.success("Deleted");
      nav("/coach/calendar");
    } catch (_) { toast.error("Failed"); }
  };

  const removeSeries = async () => {
    if (!window.confirm("Delete this lesson AND all future occurrences in the series?")) return;
    try {
      const { data } = await api.delete(`/lessons/${id}`, { params: { series: true } });
      toast.success(`Deleted ${data.deleted || 0} lessons in series`);
      nav("/coach/calendar");
    } catch (_) { toast.error("Failed"); }
  };

  const cancel = async () => {
    setCancelBusy(true);
    try {
      await api.post(`/lessons/${id}/cancel`, { reason: cancelReason, notify: true });
      toast.success("Lesson cancelled — student notified");
      setShowCancel(false); setCancelReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel");
    }
    setCancelBusy(false);
  };

  const uncancel = async () => {
    try {
      await api.post(`/lessons/${id}/uncancel`);
      toast.success("Lesson restored");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not restore");
    }
  };

  const openReschedule = () => {
    setResched({
      date: toDateInput(lesson.scheduled_at),
      time: toTimeInput(lesson.scheduled_at),
      notify: true,
    });
    setShowReschedule(true);
  };

  const reschedule = async () => {
    const iso = combineNaiveIso(resched.date, resched.time);
    if (!iso) { toast.error("Pick a date and time"); return; }
    setReschedBusy(true);
    try {
      await api.post(`/lessons/${id}/reschedule`, { scheduled_at: iso, notify: resched.notify });
      toast.success(resched.notify ? "Rescheduled — student notified" : "Rescheduled");
      setShowReschedule(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reschedule");
    }
    setReschedBusy(false);
  };

  const addTextNote = async () => {
    if (!noteText.trim()) { toast.error("Write something first"); return; }
    setNotePosting(true);
    try {
      const { data } = await api.post(`/lessons/${id}/notes`, { text: noteText, summarize: notePolish });
      setNotesLog((cur) => [...cur, data]);
      setNoteText("");
      toast.success("Note saved");
    } catch (_) { toast.error("Could not save note"); }
    setNotePosting(false);
  };

  const submitVoice = async (blob, filename) => {
    setVoiceBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, filename || "recording.webm");
      fd.append("summarize", notePolish ? "true" : "false");
      const { data } = await api.post(`/lessons/${id}/notes/voice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNotesLog((cur) => [...cur, data]);
      toast.success("Transcribed & saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transcription failed");
    }
    setVoiceBusy(false);
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/lessons/${id}/notes/${noteId}`);
      setNotesLog((cur) => cur.filter((n) => n.id !== noteId));
      toast.success("Removed");
    } catch (_) { toast.error("Could not delete"); }
  };

  if (!lesson) return <div className="text-[#A3A3A3]">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="coach-lesson-detail">
      <div>
        <Link to="/coach/calendar" className="text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white" data-testid="back-calendar-link">← Calendar</Link>
        <div className="flex flex-wrap items-start justify-between gap-4 mt-2">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">{lesson.type.replace("_", " ")} · {lesson.status}</div>
            <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">{lesson.title}</h1>
            <div className="text-sm text-[#A3A3A3] mt-1">
              {new Date(lesson.scheduled_at).toLocaleString()} · {lesson.duration_mins}min
              {student ? ` · with ${student.name}` : ""}
              {lesson.location ? ` · ${lesson.location}` : ""}
              {lesson.courts && lesson.courts > 0 ? ` · ${lesson.courts} court${lesson.courts > 1 ? "s" : ""}` : ""}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {lesson.status !== "completed" && lesson.status !== "cancelled" && (
              <>
                <button onClick={openReschedule} className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="reschedule-lesson-button">
                  <CalendarClock size={14} /> Reschedule
                </button>
                <button onClick={() => setShowCancel(true)} className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="cancel-lesson-button">
                  <Ban size={14} /> Cancel
                </button>
                <button onClick={complete} className="btn-primary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="complete-lesson-button">
                  <CheckCircle2 size={14} /> Mark complete
                </button>
              </>
            )}
            {lesson.status === "cancelled" && (
              <>
                <button onClick={openReschedule} className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="reschedule-lesson-button">
                  <CalendarClock size={14} /> Reschedule
                </button>
                <button onClick={uncancel} className="btn-primary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="uncancel-lesson-button">
                  <Undo2 size={14} /> Restore
                </button>
              </>
            )}
            {lesson.status === "completed" && (
              <button onClick={uncomplete} className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="uncomplete-lesson-button">
                <Undo2 size={14} /> Reopen
              </button>
            )}
            <button onClick={remove} className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="delete-lesson-button">
              <Trash2 size={14} /> Delete
            </button>
            {lesson.series_id && (
              <button onClick={removeSeries} className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2 !border-red-400/50 text-red-300 hover:!bg-red-500/10" data-testid="delete-series-button">
                <Trash2 size={14} /> Delete series
              </button>
            )}
          </div>
        </div>
      </div>

      {lesson.status === "completed" && (
        <div className="border border-emerald-400/40 bg-emerald-500/10 p-5 flex items-start gap-4" data-testid="completed-banner">
          <CheckCircle2 size={22} className="text-emerald-300 mt-1" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-bold">Completed</div>
            <div className="text-sm mt-1">
              Marked complete{lesson.completed_at ? ` on ${new Date(lesson.completed_at).toLocaleString()}` : ""}.
            </div>
            <div className="text-xs text-[#A3A3A3] mt-2">Use Reopen from the buttons above if this was marked in error.</div>
          </div>
        </div>
      )}


      {lesson.status === "cancelled" && (
        <div className="border border-red-400/40 bg-red-500/10 p-5 flex items-start gap-4" data-testid="cancelled-banner">
          <Ban size={22} className="text-red-300 mt-1" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-300 font-bold">Cancelled</div>
            <div className="text-sm mt-1">
              This lesson was cancelled{lesson.cancelled_at ? ` on ${new Date(lesson.cancelled_at).toLocaleString()}` : ""}.
            </div>
            {lesson.cancel_reason && (
              <div className="text-xs text-white/80 mt-1"><span className="text-[#A3A3A3] uppercase tracking-widest">Reason:</span> {lesson.cancel_reason}</div>
            )}
            <div className="text-xs text-[#A3A3A3] mt-2">The student has been notified. You can restore or reschedule from the buttons above.</div>
          </div>
        </div>
      )}

      {showReschedule && (
        <div className="card-surface p-6 border border-[#38BDF8]/40" data-testid="reschedule-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold flex items-center gap-2"><CalendarClock size={12} /> Reschedule</div>
              <h3 className="font-display text-2xl font-black uppercase tracking-tighter mt-1">Pick a new time</h3>
            </div>
            <button onClick={() => setShowReschedule(false)} className="text-[#A3A3A3] hover:text-white text-sm" data-testid="reschedule-close">Close</button>
          </div>
          <div className="mt-4 grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Date</label>
              <input type="date" value={resched.date} onChange={(e) => setResched({ ...resched, date: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
                data-testid="reschedule-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Time</label>
              <input type="time" value={resched.time} onChange={(e) => setResched({ ...resched, time: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
                data-testid="reschedule-time" />
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] self-end pb-2" data-testid="reschedule-notify-label">
              <input type="checkbox" checked={resched.notify} onChange={(e) => setResched({ ...resched, notify: e.target.checked })} className="accent-[#38BDF8]" data-testid="reschedule-notify" />
              Email the student
            </label>
          </div>
          {lesson.series_id && (
            <p className="text-[11px] text-[#A3A3A3] mt-3">
              This lesson is part of a recurring series — only this occurrence will move. Future dates remain untouched.
            </p>
          )}
          <button onClick={reschedule} disabled={reschedBusy} className="btn-primary mt-4 px-5 py-2.5 text-xs" data-testid="reschedule-submit">
            {reschedBusy ? "Saving…" : "Confirm reschedule"}
          </button>
        </div>
      )}

      {showCancel && (
        <div className="card-surface p-6 border border-red-400/40" data-testid="cancel-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-red-300 font-bold flex items-center gap-2"><Ban size={12} /> Cancel this occurrence</div>
              <h3 className="font-display text-2xl font-black uppercase tracking-tighter mt-1">Let your student know</h3>
            </div>
            <button onClick={() => setShowCancel(false)} className="text-[#A3A3A3] hover:text-white text-sm" data-testid="cancel-close">Close</button>
          </div>
          <label className="block mt-4 text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Reason (optional — sent to the student)</label>
          <input
            type="text" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Court closed, travel, weather…"
            className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
            data-testid="cancel-reason-input"
          />
          {lesson.series_id && (
            <p className="text-[11px] text-[#A3A3A3] mt-3">Only this occurrence is cancelled. Future lessons in the series stay on the calendar.</p>
          )}
          <button onClick={cancel} disabled={cancelBusy} className="btn-primary mt-4 px-5 py-2.5 text-xs !bg-red-500 hover:!bg-red-400" data-testid="cancel-confirm">
            {cancelBusy ? "Cancelling…" : "Confirm cancellation"}
          </button>
        </div>
      )}

      {/* Lesson plan */}
      <div className="card-surface p-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Lesson Plan</div>
            <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">Prepare the session</h3>
            {rosterCapable && (
              <p className="text-xs text-[#38BDF8]/80 mt-2" data-testid="roster-aware-hint">
                Group or small-group lesson — AI draft will blend each booked player's level and goals into the plan.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowBrief((v) => !v)}
              className="btn-secondary px-4 py-3 text-xs"
              data-testid="toggle-plan-brief"
            >
              {showBrief ? "Hide brief" : "Plan brief"}
            </button>
            <button onClick={generatePlan} disabled={genLoading} className="btn-primary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="generate-plan-button">
              <Sparkles size={14} /> {genLoading ? "Generating…" : "AI draft"}
            </button>
          </div>
        </div>

        {showBrief && (
          <div className="mt-5 border border-white/10 p-5" data-testid="plan-brief-panel">
            <p className="text-xs text-[#A3A3A3] mb-4">
              Everything here is sent to the AI. The values start from this event — change any of them for this draft.
            </p>
            <PlanBriefFields
              value={brief}
              onChange={setBrief}
              students={students}
              isGroup={rosterCapable}
              showStudentPicker
              showTitle
            />
          </div>
        )}

        <textarea
          rows={14}
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          placeholder="Warm-up, drills, scenarios, cool-down, homework…"
          className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-4 text-sm mt-4 font-mono"
          data-testid="lesson-plan-textarea"
        />
        <button onClick={savePlan} className="btn-secondary mt-4 px-5 py-3 text-xs" data-testid="save-plan-button">Save plan</button>

        {plan && (
          <div className="mt-6 border-t border-white/10 pt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] font-bold mb-3">Preview</div>
            <div className="prose prose-invert max-w-none text-sm">
              <Markdown>{plan}</Markdown>
            </div>
          </div>
        )}
      </div>

      {/* Group roster progress log — group events and small-group private lessons */}
      {rosterCapable && (
        <RosterProgressCard lessonId={id} onSaved={load} />
      )}

      {/* Session log — voice + text */}
      <div className="card-surface p-8" data-testid="notes-log-card">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Session Log</div>
        <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">Voice or text notes</h3>
        <p className="text-xs text-[#A3A3A3] mt-2">
          Talk it through on the court — we'll transcribe with Whisper. Every entry is timestamped and kept as history.
        </p>

        <label className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] mt-4">
          <input
            type="checkbox" checked={notePolish} onChange={(e) => setNotePolish(e.target.checked)}
            className="accent-[#38BDF8]"
            data-testid="note-polish-toggle"
          />
          AI clean-up (polish into coach-style bullets + next-focus line)
        </label>

        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-2 inline-flex items-center gap-2">
              <Mic size={12} /> Voice
            </div>
            <VoiceRecorder onSubmit={submitVoice} busy={voiceBusy} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-2 inline-flex items-center gap-2">
              <FileText size={12} /> Text
            </div>
            <textarea
              rows={5}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Quick note — what happened, what to try next…"
              className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm"
              data-testid="note-text-input"
            />
            <button
              onClick={addTextNote}
              disabled={notePosting}
              className="btn-primary mt-3 px-5 py-2 text-xs"
              data-testid="note-text-save"
            >
              {notePosting ? "Saving…" : "Add note"}
            </button>
          </div>
        </div>

        <div className="mt-8 border-t border-white/10 pt-6">
          <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-4">History ({notesLog.length})</div>
          {notesLog.length === 0 ? (
            <div className="text-sm text-[#A3A3A3]">No notes yet. Record or type above.</div>
          ) : (
            <div className="space-y-4" data-testid="notes-log-list">
              {notesLog.slice().reverse().map((n) => (
                <div key={n.id} className="border-l-2 border-[#38BDF8] pl-4 py-1" data-testid={`note-${n.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
                      {new Date(n.created_at).toLocaleString()} · {n.source === "voice" ? "🎙 voice" : "📝 text"}
                    </div>
                    <button onClick={() => deleteNote(n.id)} className="text-[#A3A3A3] hover:text-red-400" title="Delete" data-testid={`note-delete-${n.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                  {n.summary && (
                    <div className="prose prose-invert max-w-none text-sm mt-2">
                      <Markdown>{n.summary}</Markdown>
                    </div>
                  )}
                  <div className="text-xs text-[#A3A3A3] mt-2 whitespace-pre-wrap">
                    <span className="text-[10px] uppercase tracking-widest text-[#6b6b6b] mr-2">Raw</span>
                    {n.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Observations + AI insights */}
      <div className="card-surface p-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Post-Session</div>
        <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">Observations & insights</h3>

        <textarea
          rows={6}
          value={observations}
          onChange={(e) => setObservations(e.target.value)}
          placeholder="What went well, what to work on next…"
          className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-4 text-sm mt-4"
          data-testid="observations-textarea"
        />
        <button onClick={generateInsight} disabled={insightLoading} className="btn-primary mt-4 px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="generate-insight-button">
          <Sparkles size={14} /> {insightLoading ? "Thinking…" : "AI insights"}
        </button>

        {insights && (
          <div className="mt-6 border-t border-white/10 pt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] font-bold mb-3">AI takeaways</div>
            <div className="prose prose-invert max-w-none text-sm">
              <Markdown>{insights}</Markdown>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
